<script>
// Texto do estado inconsistente (spec-011 [T063]): o backend impede
// `sem_divergencia=false` com `dissenting_agents` vazio pelo CHECK
// `ck_intelligence_v2_advisory_rec_...` (data-model.md), mas o componente é
// BURRO e não conhece esse contrato — ele nunca afirma "consenso" por
// omissão. Constante nomeada em vez de string solta no template (regra de
// magic value do repo).
const TEXTO_ESTADO_INCONSISTENTE =
  'Divergência não registrada e consenso não confirmado. Contate o suporte.'
const TEXTO_CONSENSO_DECLARADO = 'Consenso declarado'
// S-3 (review front it.3): o título do bloco NUNCA pode contradizer o
// estado que ele rotula — "Divergência" em cima de "Consenso declarado"
// lia como o oposto do que a tela afirmava.
const TEXTO_TITULO_CONSENSO = 'Consenso'
const TEXTO_TITULO_DIVERGENCIA = 'Divergência em aberto'

export default {
  name: 'IntelligenceV2PainelDivergencias',
  props: {
    // Já decoradas por `decorarDivergencias` — cada uma traz as áreas em texto,
    // a natureza em português e a saída (condição de dispensa OU caminho de
    // resolução, conforme o shape do parecer).
    divergencias: {
      type: Array,
      required: true,
    },
    // Prop aditiva (spec-011 [T063], `ui-components.md` §3): `true` quando a
    // recomendação do comitê declarou `sem_divergencia=true` — o selo de
    // consenso substitui o bloco de divergências. Default `false` preserva o
    // comportamento atual (quem não passa a prop desenha exatamente como
    // antes).
    semDivergencia: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    // O par `semDivergencia=false` + `divergencias=[]` é IMPOSSÍVEL no banco
    // (o CHECK exige `dissenting_agents` não-vazio quando não há consenso),
    // mas o componente não lê banco — ele só sabe o que recebeu. Sem esta
    // guarda, a ausência de itens desenharia um bloco vazio que lê como
    // "nada a reportar", quando na verdade os dados são inconsistentes.
    estadoInconsistente() {
      return !this.semDivergencia && this.divergencias.length === 0
    },
    textoEstadoInconsistente() {
      return TEXTO_ESTADO_INCONSISTENTE
    },
    textoConsensoDeclarado() {
      return TEXTO_CONSENSO_DECLARADO
    },
    tituloConsenso() {
      return TEXTO_TITULO_CONSENSO
    },
    tituloDivergencia() {
      return TEXTO_TITULO_DIVERGENCIA
    },
  },
}
</script>

<template>
  <!-- Divergência é output do painel, não erro: quando duas áreas não se
       acertam, o gestor precisa ver onde e sob que condição isso cai. -->
  <div class="painel-divergencias az-gap-small">
    <template v-if="semDivergencia">
      <div class="az-ds-label-sentenca painel-divergencias__titulo">
        {{ tituloConsenso }}
      </div>
      <div class="d-flex align-center az-gap-xsmall" data-test="painel-divergencias-consenso">
        <icon-svg name="check-circle" :size="14" color="var(--success)" class="flex-shrink-0" />
        <BaseStatus text preserve-case :content="textoConsensoDeclarado" color="success" />
      </div>
    </template>
    <template v-else>
      <div class="az-ds-label-sentenca painel-divergencias__titulo">
        {{ tituloDivergencia }}
      </div>
      <div
        v-if="estadoInconsistente"
        class="d-flex align-center az-gap-xsmall painel-divergencias__inconsistencia"
        data-test="painel-divergencias-inconsistencia"
      >
        <icon-svg name="error-circle" :size="14" color="var(--danger)" class="flex-shrink-0" />
        <span class="az-text-secondary az-size-12">{{ textoEstadoInconsistente }}</span>
      </div>
      <div
        v-for="divergencia in divergencias"
        :key="divergencia.chave"
        class="painel-divergencias__item az-gap-xsmall"
      >
        <!-- `natureza` é OPCIONAL: quem decora as divergências da Sala do
             Comitê ao vivo (`decorarDivergencias`) sempre a preenche, mas o
             Advisory Hub (`divergenciasDoAdvisoryParaPainel`, sala do
             engagement persistido) só tem `dissentingAgents` — nomes, sem
             classificação por natureza. -->
        <div class="az-text-primary az-size-12 font-medium">
          {{ divergencia.areas }}<template v-if="divergencia.natureza"> — natureza: {{ divergencia.natureza }}</template>
        </div>
        <div v-if="divergencia.descricao" class="az-text-secondary az-size-12">
          {{ divergencia.descricao }}
        </div>
        <!-- A derivação determinística aponta a condição que DERRUBA a
             restrição; o Orquestrador aponta o que FAZER para resolver. São
             coisas diferentes, e por isso cada uma tem o seu rótulo. -->
        <div v-if="divergencia.condicao" class="az-text-secondary az-size-12 az-muted">
          Cai se: {{ divergencia.condicao }}
        </div>
        <div v-if="divergencia.caminho" class="az-text-secondary az-size-12 az-muted">
          Como resolver: {{ divergencia.caminho }}
        </div>
      </div>
    </template>
  </div>
</template>

<style lang="sass" scoped>
.painel-divergencias
  display: flex
  flex-direction: column

.painel-divergencias__item
  display: flex
  flex-direction: column
  border-left: 2px solid var(--gray-3)
  padding-left: 8px
</style>
